class ShopfloTheme {
  constructor(config) {
    this.config = config || window.shopfloThemeConfig || {};
    this.buyNowButton = document.getElementById('flo-buy-now-button');
    this.nativeBuyNowWrapper = document.getElementById('shopify-buy-now__button--wrapper');

    this.init();
  }

  init() {
    // Master on/off (window.shopfloThemeConfig.shopflo_enable, hardcoded in
    // snippets/shopflo.liquid - NOT a Theme Editor setting) is deliberately NOT checked here.
    // The checkout/buy-now/cart markup always renders the same way regardless, so every binding
    // below must stay active either way - shouldTriggerFloDirectly() is what actually decides,
    // at click time, whether to open Shopflo's real flow or fall back to a plain navigation.
    this.syncCartAutoOpenGlobal();
    this.bindGlobalTriggers();
    this.bindBuyNowAtcSync();
    this.bindBuyNowIntlFallback();
    this.bindDomEvents();
    this.bindCartIconTriggers();
    this.bindPopupMorph();
  }

  isDomesticTimezone() {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone === 'Asia/Calcutta' || timeZone === 'Asia/Kolkata';
  }

  // The single click-time decision point: should this interaction go through Shopflo's real
  // bundle, or fall back to a plain native action? False when Shopflo is disabled entirely
  // (this.config.shopflo_enable), OR when the shopper should be internationally redirected
  // instead (shopflo_international_redirect_enabled and not on a domestic timezone).
  shouldTriggerFloDirectly() {
    if (this.config.shopflo_enable === false) return false;
    return !this.config.shopflo_international_redirect_enabled || this.isDomesticTimezone();
  }

  // cart-drawer.js / cart-notification.js read this global directly to decide whether to auto-open after add-to-cart
  syncCartAutoOpenGlobal() {
    window.shopfloCartAutoOpen = this.config.shopflo_cart_open_after_atc !== false;
  }

  // Dispatched right before Shopflo's real checkout overlay actually opens - whether triggered
  // by the checkout button or a buy-now button, both open the same underlying checkout - so any
  // other modal/popup on the page can listen and close itself instead of sitting behind it.
  // Not fired on the native-navigation fallback (shouldTriggerFloDirectly() false): the page is
  // leaving entirely there, so there's nothing in-page left to close.
  dispatchCheckoutOpened() {
    document.dispatchEvent(new CustomEvent('shopflo-event:checkout-opened'));
  }

  // Dispatched right before Shopflo's real cart overlay actually opens - cart-icon click or
  // auto-open after add-to-cart - same reasoning as dispatchCheckoutOpened() above.
  dispatchCartOpened() {
    document.dispatchEvent(new CustomEvent('shopflo-event:cart-opened'));
  }

  // Named openThemeFloCheckout (not openFloCheckout) deliberately - same reasoning as
  // openThemeFloCart below: the Shopflo bundle script defines its own globals
  // (handleFloCheckoutBtn, etc.); prefixing ours with "Theme" avoids silently colliding with
  // or overriding a same-named function the bundle might also expose.
  openThemeFloCheckout() {
    if (this.shouldTriggerFloDirectly()) {
      if (typeof window.handleFloCheckoutBtn === 'function') {
        this.dispatchCheckoutOpened();
        window.handleFloCheckoutBtn();
      }
    } else {
      window.location.href = '/checkout';
    }
  }

  // Named openThemeFloCart (not openFloCart) deliberately - the Shopflo bundle script defines
  // its own globals (handleFloCartBtn, etc.); prefixing ours with "Theme" avoids silently
  // colliding with or overriding a same-named function the bundle might also expose.
  openThemeFloCart() {
    if (this.shouldTriggerFloDirectly()) {
      // Always a plain, foreground open - `loadCartInBackground: true` tells the bundle to load
      // WITHOUT showing anything, so passing it here would silently do nothing on click. Any
      // background warming for "Cart Click" mode happens earlier, at add-to-cart time - see
      // bindDomEvents().
      if (typeof window.handleFloCartBtn === 'function') {
        this.dispatchCartOpened();
        window.handleFloCartBtn();
      }
    } else {
      window.location.href = '/cart';
    }
  }

  // Named openThemeFloBuyNow (not openFloBuyNow) deliberately - same reasoning as
  // openThemeFloCheckout/openThemeFloCart above. Centralizes the same shouldTriggerFloDirectly()
  // gate those two already have - buy-now itself opens the same underlying checkout as the
  // checkout button, so its native fallback is the same plain '/checkout' navigation, not '/cart'.
  // Every call site that could otherwise reach window.handleFloBuyNowBtn directly (see
  // bindPopupMorph()'s reduceMotion branch and click gate, and runPendingFloAction() below) must
  // go through this instead - calling the bundle function unconditionally would open Shopflo's
  // real buy-now flow even for a shopper who should be internationally redirected to native
  // checkout instead.
  openThemeFloBuyNow(originEvent) {
    if (this.shouldTriggerFloDirectly()) {
      if (typeof window.handleFloBuyNowBtn === 'function') {
        this.dispatchCheckoutOpened();
        window.handleFloBuyNowBtn(originEvent);
      }
    } else {
      window.location.href = '/checkout';
    }
  }

  // snippets/shopflo.liquid renders onclick="openThemeFloCheckout()" as a plain global call
  bindGlobalTriggers() {
    window.openThemeFloCheckout = this.openThemeFloCheckout.bind(this);
    window.openThemeFloCart = this.openThemeFloCart.bind(this);
  }

  // keeps the Buy Now button's disabled state in sync with the product form's Add to Cart
  // button as the shopper switches variants, on top of the initial server-rendered state
  bindBuyNowAtcSync() {
    if (!this.buyNowButton) return;

    const scope =
      this.buyNowButton.closest('form[action*="/cart/add"]') ||
      this.buyNowButton.closest('product-info, .product, [data-section-type="product"], section') ||
      document;
    const atcButton =
      scope.querySelector('button[name="add"], [name="add"]:not([type="hidden"])') ||
      document.querySelector(
        'form[action*="/cart/add"] [name="add"], form[action*="/cart/add"] button[type="submit"]'
      );
    if (!atcButton) return;

    const sync = () => {
      this.buyNowButton.disabled = atcButton.disabled || atcButton.getAttribute('aria-disabled') === 'true';
    };
    sync();
    new MutationObserver(sync).observe(atcButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
    });
  }

  // internationally redirected shoppers fall back to Shopify's native dynamic checkout button
  // instead of the Flo buy-now button
  bindBuyNowIntlFallback() {
    if (!this.buyNowButton || !this.nativeBuyNowWrapper) return;
    if (!window.location.pathname.includes('products')) return;

    if (this.shouldTriggerFloDirectly()) {
      this.buyNowButton.style.setProperty('display', 'flex', 'important');
      this.nativeBuyNowWrapper.style.setProperty('display', 'none', 'important');
    } else {
      this.nativeBuyNowWrapper.style.setProperty('display', 'block', 'important');
      this.buyNowButton.style.setProperty('display', 'none', 'important');
    }
  }

  // shopflo-event:add-to-cart fires right after every successful add-to-cart (see
  // assets/cart-drawer.js / assets/cart-notification.js), regardless of which Cart behavior
  // (Theme Editor > Shopflo > Cart behavior) is configured:
  // - "Auto Open" (shopflo_cart_open_after_atc !== false): open the cart immediately.
  // - "Cart Click" (shopflo_cart_open_after_atc === false): must NOT show the cart yet, but
  //   still tell the bundle to load its content in the background so the eventual cart-icon
  //   click (see bindCartIconTriggers() / openThemeFloCart()) opens instantly instead of
  //   loading cold.
  bindDomEvents() {
    document.addEventListener('shopflo-event:add-to-cart', () => {
      if (!this.shouldTriggerFloDirectly()) {
        return;
      }
      if (typeof window.handleFloCartBtn !== 'function') {
        return;
      }
      if (this.config.shopflo_cart_open_after_atc === false) {
        window.handleFloCartBtn(null, { loadCartInBackground: true });
      } else {
        this.dispatchCartOpened();
        window.handleFloCartBtn();
      }
    });
  }

  // Any element marked data-js-shopflo-cart-icon (see sections/header.liquid) opens Shopflo's
  // cart on click - no custom event involved, just a direct delegated listener, so markup only
  // needs the one data attribute and no onclick/JS wiring of its own. Always a plain open (see
  // openThemeFloCart()) - the bundle's own content is already warm by now regardless of Cart
  // behavior, either from the "Auto Open" open itself or the "Cart Click" background load above.
  bindCartIconTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-js-shopflo-cart-icon]');
      if (!trigger) return;
      event.preventDefault();
      this.openThemeFloCart();
    });
  }

  // Drives the shared dummy popup (#popupOverlay/#popupWrapper, rendered once in shopflo.liquid)
  // through a button<->full-screen FLIP animation that we fully own, instead of trying to morph
  // Shopflo's own injected checkout markup (fragile: we don't control its structure or timing).
  // Buttons carry .shopflo-popup-trigger + data-flo-action="checkout|buy-now" instead of onclick,
  // so any number of Buy Now / Checkout buttons on the page can share this one popup. Only once
  // the OPEN transition finishes do we hand off to the real Shopflo checkout; closing is driven by
  // Shopflo's "FLO_EXIT_CHECKOUT" postMessage, which shrinks the popup back to its trigger button.
  bindPopupMorph() {
    // reduceMotion covers both the OS-level accessibility preference and the Theme Editor's
    // "Enable checkout popup animation" toggle. With no animation to play, the dummy popup adds
    // nothing (it would just flash open then immediately hand off) - so skip it and its whole
    // DOM/JS machinery entirely, and call Shopflo's real functions directly on click, same as
    // before the popup-morph system existed.
    this.reduceMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      this.config.shopflo_enable_popup_animation === false;

    if (this.reduceMotion) {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('.shopflo-popup-trigger');
        if (!trigger || trigger.disabled) return;
        if (trigger.dataset.floAction === 'buy-now') {
          this.openThemeFloBuyNow(event);
        } else {
          this.openThemeFloCheckout();
        }
      });
      return;
    }

    this.popupOverlay = document.getElementById('popupOverlay');
    this.popupWrapper = document.getElementById('popupWrapper');
    if (!this.popupOverlay || !this.popupWrapper) return;

    this.popupAnimating = false;
    this.activePopupTrigger = null;
    this.pendingFloAction = null;
    this.pendingFloEvent = null;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.shopflo-popup-trigger');
      if (!trigger || trigger.disabled || this.popupAnimating) return;

      // Matches openThemeFloCheckout()/openThemeFloBuyNow()'s own fallback branch (disabled
      // entirely, or an internationally-redirected shopper): a full page navigation is instant,
      // so there's nothing for the popup to usefully animate into - navigate directly instead.
      // Both actions fall back to the same '/checkout' - buy-now opens the same underlying
      // checkout as the checkout button, just with a single product instead of the full cart.
      if (!this.shouldTriggerFloDirectly()) {
        window.location.href = '/checkout';
        return;
      }

      this.openPopupFrom(trigger, event);
    });

    const closeBtn = document.getElementById('popupClose');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closePopup());

    this.popupOverlay.addEventListener('click', (event) => {
      if (event.target === this.popupOverlay) this.closePopup();
    });

    window.addEventListener('message', (event) => this.handleFloMessage(event));
  }

  handleFloMessage(event) {
    const data = event.data;
    const type = typeof data === 'string' ? data : data && (data.type || data.event || data.name);
    if (type === 'FLO_EXIT_CHECKOUT') this.closePopup();
  }

  // ratio of the trigger button's on-screen box to the popup's fixed natural width, expressed as
  // a translate+scale so `transform-origin: 0 0` makes the popup sit exactly over the button.
  // Height is NOT scaled here - it's a real property transition (see triggerHeightFor/--h) so it
  // doesn't distort border-radius/content the way non-uniform scale(sx, sy) would.
  popupRectFor(trigger) {
    const triggerRect = trigger.getBoundingClientRect();
    const popupW = this.popupWrapper.offsetWidth;
    return {
      x: triggerRect.left,
      y: triggerRect.top,
      sx: popupW ? triggerRect.width / popupW : 1,
    };
  }

  setPopupTransformVars(rect) {
    this.popupWrapper.style.setProperty('--tx', `${rect.x}px`);
    this.popupWrapper.style.setProperty('--ty', `${rect.y}px`);
    this.popupWrapper.style.setProperty('--sx', String(rect.sx));
  }

  // real (px) height of whichever button triggered the popup
  triggerHeightFor(trigger) {
    return `${trigger.getBoundingClientRect().height}px`;
  }

  // the popup's own resting height (--popup-h, responsive per breakpoint), read as a concrete
  // value so it can be transitioned to/from
  targetPopupHeight() {
    return getComputedStyle(this.popupWrapper).getPropertyValue('--popup-h').trim() || '100%';
  }

  setPopupHeight(height) {
    this.popupWrapper.style.setProperty('--h', height);
  }

  // the popup's FLIP start/end radius - matches the trigger button's own live computed
  // border-radius exactly, so the popup starts (open) / ends (close) looking like the button
  triggerRadiusFor(trigger) {
    return getComputedStyle(trigger).borderRadius || '0px';
  }

  // the popup's own resting radius (--popup-radius, responsive per breakpoint - see
  // shopflo-css-variables.liquid), read as a concrete value so it can be transitioned to/from
  targetPopupRadius() {
    return getComputedStyle(this.popupWrapper).getPropertyValue('--popup-radius').trim() || '0px';
  }

  setPopupRadius(radius) {
    this.popupWrapper.style.setProperty('--radius', radius);
  }

  // total morph duration in ms, read from --duration so the opacity edge below always tracks it
  getDurationMs() {
    const raw = getComputedStyle(this.popupWrapper).getPropertyValue('--duration').trim();
    const ms = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
    return Number.isFinite(ms) && ms > 0 ? ms : 500;
  }

  // timing for the popup wrapper's OWN opacity edge-fade: a short ~8% slice of the overall
  // morph, positioned at the very start (edge:'start') or very end (edge:'end') of --duration.
  // 8% (~40ms on the current 550ms --duration) reads fine here because it's layered on top of
  // a transform/radius change that's already visibly moving - the eye reads the opacity shift
  // as part of that motion rather than judging it as its own fade.
  edgeFadeTiming(edge) {
    const total = this.getDurationMs();
    const edgeDuration = Math.round(total * 0.08);
    const delay = edge === 'end' ? total - edgeDuration : 0;
    return { edgeDuration, delay };
  }

  // Shared close-only crossfade window: the popup wrapper (opaque, sitting on top of the
  // button z-index-wise) and the trigger button MUST fade on the exact same duration/delay, or
  // the still-opaque wrapper simply masks however far the button has already faded in - which
  // is what made it look like the button "pops in" only after the shrink visually finishes.
  closeFadeTiming() {
    const duration = 1950;
    const delay = Math.max(0, this.getDurationMs() - duration);
    return { duration, delay };
  }

  // the opacity fade should only cover a short slice of the overall transform/radius morph on
  // open, not the whole duration - so it reads as a quick flourish rather than a slow cross-fade.
  // On close, it's synced with fadeTrigger's window instead (see closeFadeTiming).
  setPopupOpacityTiming(edge) {
    if (edge === 'end') {
      const { duration, delay } = this.closeFadeTiming();
      this.popupWrapper.style.setProperty('--opacity-duration', `${duration}ms`);
      this.popupWrapper.style.setProperty('--opacity-delay', `${delay}ms`);
      return;
    }
    const { edgeDuration, delay } = this.edgeFadeTiming(edge);
    this.popupWrapper.style.setProperty('--opacity-duration', `${edgeDuration}ms`);
    this.popupWrapper.style.setProperty('--opacity-delay', `${delay}ms`);
  }

  // hides the real trigger button within a 50ms window at the very start of the expand
  // (edge:'start', open). On close (edge:'end'), it fades in over the same window the popup
  // wrapper itself is fading out over (closeFadeTiming), so both happen as one crossfade.
  fadeTrigger(trigger, edge, targetOpacity) {
    if (edge === 'end') {
      const { duration, delay } = this.closeFadeTiming();
      trigger.style.transition = `opacity ${duration}ms linear ${delay}ms`;
      trigger.style.opacity = String(targetOpacity);
      return;
    }
    trigger.style.transition = 'opacity 50ms linear';
    trigger.style.opacity = String(targetOpacity);
  }

  openPopupFrom(trigger, originClickEvent) {
    this.popupAnimating = true;
    this.activePopupTrigger = trigger;
    this.pendingFloAction = trigger.dataset.floAction;
    this.pendingFloEvent = originClickEvent;

    this.showPopupIfHidden();
    this.popupOverlay.classList.add('is-open');
    trigger.classList.add('sf-popup-trigger--hidden');

    this.popupWrapper.style.transition = 'none';
    this.setPopupTransformVars(this.popupRectFor(trigger));
    this.setPopupRadius(this.triggerRadiusFor(trigger));
    this.setPopupHeight(this.triggerHeightFor(trigger));
    this.popupWrapper.style.opacity = '0';
    void this.popupWrapper.offsetWidth; // force layout so the "start" position paints before we animate away from it

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Measure the natural resting height/width while transitions are STILL disabled (from
        // the instant-jump phase above), so offsetHeight reports the real target height rather
        // than a transition's in-progress (starting) value.
        this.setPopupHeight(this.targetPopupHeight());
        const popupW = this.popupWrapper.offsetWidth;
        const popupH = this.popupWrapper.offsetHeight;

        // Restore --h to the trigger's height and FORCE A FLUSH here (still under
        // transition:none) - without this, the browser's next transition comparison would use
        // the measurement snapshot above (already at the target) as its baseline, see no net
        // change once --h is set to the target again below, and silently skip animating height
        // entirely (it would just snap to full size on frame one).
        this.setPopupHeight(this.triggerHeightFor(trigger));
        void this.popupWrapper.offsetHeight;

        this.popupWrapper.style.transition = '';
        this.popupOverlay.classList.add('active');

        this.setPopupTransformVars({
          x: (window.innerWidth - popupW) / 2,
          y: (window.innerHeight - popupH) / 2,
          sx: 1,
        });
        this.setPopupRadius(this.targetPopupRadius());
        this.setPopupHeight(this.targetPopupHeight());
        this.setPopupOpacityTiming('start');
        this.popupWrapper.style.opacity = '1';
        this.fadeTrigger(trigger, 'start', 0);

        // local flag (not instance state) so the transitionend/timeout race can only ever
        // run the hand-off once per cycle, regardless of what a later cycle does to `this`
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          this.popupAnimating = false;
          this.runPendingFloAction();
        };
        this.popupWrapper.addEventListener(
          'transitionend',
          (e) => {
            if (e.propertyName !== 'transform') return;
            finish();
          },
          { once: true }
        );
        // safety net in case the transition is interrupted and transitionend never fires -
        // scaled to --duration (plus a buffer) so it never fires before the real transition
        // could possibly have finished, however long --duration is set to
        setTimeout(finish, this.getDurationMs() + 150);
      });
    });
  }

  runPendingFloAction() {
    const action = this.pendingFloAction;
    const originEvent = this.pendingFloEvent;
    this.pendingFloAction = null;
    this.pendingFloEvent = null;

    if (action === 'buy-now') {
      this.openThemeFloBuyNow(originEvent);
    } else {
      this.openThemeFloCheckout();
    }

    // Shopflo's own checkout/cart overlay mounts at an extreme z-index (see its injected
    // stylesheet) so it already paints over our popup - this hide is just cleanup, giving the
    // handoff call a tick to fire before we get out of the way. Kept as `visibility` (not
    // display/is-open) so closePopup() can reveal it again for the shrink-back animation
    // without disturbing its transform/transition state.
    setTimeout(() => {
      this.popupOverlay.style.visibility = 'hidden';
    }, 10);
  }

  showPopupIfHidden() {
    if (this.popupOverlay.style.visibility === 'hidden') {
      this.popupOverlay.style.visibility = '';
    }
  }

  closePopup() {
    const trigger = this.activePopupTrigger;
    if (!trigger || this.popupAnimating) return;
    this.popupAnimating = true;

    this.showPopupIfHidden();
    this.popupOverlay.classList.remove('active');

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      this.popupOverlay.classList.remove('is-open');
      trigger.classList.remove('sf-popup-trigger--hidden');
      // release inline control back to the button's own CSS-driven transition now that the
      // crossfade is done (or was skipped) - it's already visually at full opacity by now
      trigger.style.opacity = '';
      trigger.style.transition = '';
      this.activePopupTrigger = null;
      this.popupAnimating = false;
    };

    if (!document.body.contains(trigger)) {
      this.popupWrapper.style.opacity = '0';
      finish();
      return;
    }

    this.setPopupTransformVars(this.popupRectFor(trigger));
    this.setPopupRadius(this.triggerRadiusFor(trigger));
    this.setPopupHeight(this.triggerHeightFor(trigger));
    this.setPopupOpacityTiming('end');
    this.popupWrapper.style.opacity = '0';
    this.fadeTrigger(trigger, 'end', 1);
    this.popupWrapper.addEventListener(
      'transitionend',
      (e) => {
        if (e.propertyName !== 'transform') return;
        finish();
      },
      { once: true }
    );
    // safety net: a transition can be silently aborted (no transitionend fired) - scaled to
    // --duration (plus a buffer) so it never fires before the real transition could possibly
    // have finished, however long --duration is set to
    setTimeout(finish, this.getDurationMs() + 150);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.shopfloThemeInstance = new ShopfloTheme();
});

window.ShopfloTheme = ShopfloTheme;

/**
 * Shopflo Accounts (shop_pass)
 * ----------------------------------------------------------------
 * Defines <shopflo-accounts>, a custom element that renders its OWN
 * account icon/dropdown markup (rendered via
 * {% render 'shopflo', type: 'shop_pass_primary' | 'shop_pass_secondary' | 'shop_pass_tertiary' %},
 * see snippets/shopflo.liquid), driven entirely by two sessionStorage
 * keys the Shopflo bundle itself owns and keeps up to date:
 *
 *   flo_isShopfloSession — "true" when a Shopflo session exists, absent otherwise.
 *   FLO_SSO_IS_LOGOUT    — "true" right after a logout, absent otherwise.
 *
 * Design summary:
 *  - No hidden "actual" markup, no click-forwarding, no DOM
 *    observation of bundle-controlled elements. Auth state is a
 *    synchronous sessionStorage read, not something inferred from
 *    which element happens to be visible.
 *  - Confidently logged-in (session present, not mid-logout) is the
 *    ONLY state where this renders its own dropdown. Every other
 *    state (fresh session, or just logged out) defers entirely to
 *    the bundle's own window.handleDrawer() to decide what to show.
 *  - Logging out sets FLO_SSO_IS_LOGOUT and reloads the page, so
 *    every <shopflo-accounts> instance re-reads the same keys fresh
 *    on load — instances never need to be synced with each other at
 *    runtime.
 *  - Any number of <shopflo-accounts> proxy instances can exist on
 *    a page (desktop header, mobile menu, footer, ...); each is
 *    fully independent.
 *  - Proxy markup declares its role via data-flo-trigger /
 *    data-flo-state attributes. This file never hardcodes ids, so
 *    theme authors can freely add/remove/restructure proxy markup
 *    without touching this file.
 */

/**
 * Global config. Edit this object to change behavior across every
 * <shopflo-accounts> instance on the page — instance-level
 * differences (labels, initial state, markup) belong in the
 * .liquid snippet / theme settings, not here.
 */
const ShopfloAccountsConfig = {
  // Bounded readiness poll for the bundle-defined globals this file
  // calls on demand (handleDrawer / handleShopifyLogin) — the bundle
  // script may still be evaluating when the shopper clicks.
  ready: {
    intervalMs: 150,
    maxAttempts: 20, // ~3s ceiling
    onTimeout: 'warn', // 'warn' | 'silent'
  },

  // sessionStorage keys the Shopflo bundle itself owns. Both are
  // either the string "true" or absent — never a literal "false".
  session: {
    isSessionKey: 'flo_isShopfloSession',
    isLogoutKey: 'FLO_SSO_IS_LOGOUT',
  },

  // The bundle appends its OWN real login iframe on handleShopifyLogin() click.
  // DESKTOP (screen width 800px+): kept permanently off-screen (see #flo-shopify-login-iframe in
  // shopflo-styles.css) — we copy its src into our own iframe (in normal, always-visible proxy
  // markup) instead, so it can be styled/positioned freely as part of our own drawer.
  // MOBILE (up to 799px): the bundle's own self-contained sheet+overlay component is left fully
  // visible and untouched — no copying, this theme's own drawer/overlay just steps out of the
  // way entirely.
  iframe: {
    sourceId: 'flo-shopify-login-iframe',
    mobileBreakpoint: 799,
  },

  debug: false,
};

class ShopfloAccounts extends HTMLElement {
  connectedCallback() {
    // Guards against connectedCallback firing more than once for the
    // same element (e.g. if it's ever disconnected/reconnected by
    // the theme — a sticky-header clone, a section re-render, etc.).
    // Without this, a second connectedCallback would attach a SECOND
    // click listener to the same button, so one physical click could
    // fire _handleHeaderIconClick twice in the same tick — and if
    // real auth state changes between those two firings, each one
    // can resolve a different branch, producing mixed behavior.
    if (this._shopfloInitialized) return;
    this._shopfloInitialized = true;

    this._onPageShow = this._onPageShow.bind(this);
    window.addEventListener('pageshow', this._onPageShow);

    // Synchronous sessionStorage read — nothing to wait on.
    this._applyAuthState(this._resolveSessionState());

    this._setupHeaderIconTrigger();
    this._setupOverlayTrigger();
    this._setupDrawerItemTriggers();
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._onPageShow);
  }

  /**
   * Fires on every page show, including when the page is restored
   * from the browser's back-forward cache (bfcache) after the user
   * navigates away and presses back. A bfcache restore does NOT
   * re-run connectedCallback's normal setup, since the page isn't
   * reloaded, just resumed exactly as it was — so a drawer left open
   * when they left is still open when they return. This explicitly
   * resets that. event.persisted is what distinguishes a genuine
   * bfcache restore from an ordinary load, where this should do
   * nothing (normal setup already handles the closed default).
   */
  _onPageShow(event) {
    if (!event.persisted) return;
    const wasOpen = this.querySelector(
      '[data-flo-state="drawer"][data-flo-visible="true"], [data-flo-state="drawer-iframe"][data-flo-visible="true"]'
    );
    if (!wasOpen) return;
    this._closeDrawer();
  }

  _globalsReady(fnNames) {
    return fnNames.every((name) => typeof window[name] === 'function');
  }

  /**
   * Resolves once required global functions exist, or once
   * maxAttempts is exhausted (resolves false in that case).
   * Resolves immediately (true) if no globals are required.
   */
  _waitForGlobals(fnNames) {
    const readyConfig = ShopfloAccountsConfig.ready;
    return new Promise((resolve) => {
      if (!fnNames.length || this._globalsReady(fnNames)) {
        resolve(true);
        return;
      }
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (this._globalsReady(fnNames)) {
          clearInterval(timer);
          resolve(true);
        } else if (attempts >= readyConfig.maxAttempts) {
          clearInterval(timer);
          resolve(false);
        }
      }, readyConfig.intervalMs);
    });
  }

  /**
   * Resolves with the element once it exists in the DOM, or null once
   * maxAttempts is exhausted. Used to detect the bundle's own
   * dynamically-appended iframe without a MutationObserver — this is
   * a short, bounded poll only, not a continuous watch.
   */
  _waitForElement(id) {
    const readyConfig = ShopfloAccountsConfig.ready;
    return new Promise((resolve) => {
      const existing = document.getElementById(id);
      if (existing) {
        resolve(existing);
        return;
      }
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        const el = document.getElementById(id);
        if (el) {
          clearInterval(timer);
          resolve(el);
        } else if (attempts >= readyConfig.maxAttempts) {
          clearInterval(timer);
          resolve(null);
        }
      }, readyConfig.intervalMs);
    });
  }

  /**
   * Confidently 'logged-in' ONLY when a Shopflo session exists AND we're not in the brief
   * post-logout window — every other combination (both keys missing, both present/true, or only
   * FLO_SSO_IS_LOGOUT present) defers to the bundle's own window.handleDrawer() rather than
   * guessing, since that's the one thing the bundle itself always knows how to resolve correctly.
   */
  _resolveSessionState() {
    const { isSessionKey, isLogoutKey } = ShopfloAccountsConfig.session;
    const hasSession = window.sessionStorage.getItem(isSessionKey) === 'true';
    const isLoggingOut = window.sessionStorage.getItem(isLogoutKey) === 'true';
    return hasSession && !isLoggingOut ? 'logged-in' : 'logged-out';
  }

  /** Reflects a resolved auth state onto this instance's own proxy elements. */
  _applyAuthState(state) {
    const isLoggedIn = state === 'logged-in';
    this.querySelectorAll('[data-flo-state="login-icon"]').forEach((el) => {
      el.setAttribute('data-flo-visible', isLoggedIn ? 'false' : 'true');
    });
    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.setAttribute('data-flo-visible', isLoggedIn ? 'true' : 'false');
    });
  }

  _setupHeaderIconTrigger() {
    const triggers = this.querySelectorAll('[data-flo-trigger="header-icon"]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => this._handleHeaderIconClick(event));
    });
  }

  _setupOverlayTrigger() {
    const triggers = this.querySelectorAll('[data-flo-trigger="close-drawer"]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this._closeDrawer());
    });
  }

  /** Wires the drawer's own "Account" / "Log out" menu items — no bundle click-forwarding. */
  _setupDrawerItemTriggers() {
    this.querySelectorAll('[data-flo-trigger="account-login"]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        this._callGlobalWhenReady('handleShopifyLogin', [event, '/account']);
        this._handleAccountIframeFlow();
      });
    });

    this.querySelectorAll('[data-flo-trigger="account-logout"]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        // Setting FLO_SSO_IS_LOGOUT and reloading is the whole logout flow — on the next load
        // _resolveSessionState() reads it back as 'logged-out' and every instance starts fresh,
        // so there is nothing else to reset here.
        window.sessionStorage.setItem(ShopfloAccountsConfig.session.isLogoutKey, 'true');
        window.location.reload();
      });
    });
  }

  // Dispatched on `document` for shop-pass interactions, so any other modal/popup on the page
  // can listen and close itself (same reasoning as dispatchCheckoutOpened()/dispatchCartOpened()
  // in ShopfloTheme above). Not a class method there because these live on the instance for the
  // custom element instead - kept as a tiny shared helper here rather than repeating
  // `document.dispatchEvent(new CustomEvent(...))` at every call site.
  _dispatchAccountEvent(name) {
    document.dispatchEvent(new CustomEvent('shopflo-event:' + name));
  }

  /** Calls window[fnName] immediately if it already exists, else waits up to the ready ceiling. */
  _callGlobalWhenReady(fnName, args) {
    if (typeof window[fnName] === 'function') {
      window[fnName].apply(window, args);
      return;
    }
    this._waitForGlobals([fnName]).then((ok) => {
      if (!ok) {
        console.warn('[shopflo-accounts]', 'Timed out waiting for window.' + fnName + '().');
        return;
      }
      window[fnName].apply(window, args);
    });
  }

  _handleHeaderIconClick(event) {
    // Re-check right before acting, rather than trusting whatever this instance last rendered —
    // state may have changed since load.
    const state = this._resolveSessionState();
    this._applyAuthState(state);

    if (state === 'logged-in') {
      this._closeIframeFlow();
      const opening = this._isDrawerClosed(); // check BEFORE toggling
      if (opening) {
        this._positionDrawer();
      }
      this._toggleState('drawer', event.currentTarget);
      this._setOverlayVisible(opening);
      this._dispatchAccountEvent(opening ? 'account-drawer-opened' : 'account-drawer-closed');
    } else {
      // Not confidently logged-in — defer entirely to the bundle's own handleDrawer(), which
      // decides for itself whether to show its login UI or its own account drawer.
      this._dispatchAccountEvent('account-login-opened');
      this._callGlobalWhenReady('handleDrawer', []);
    }
  }

  // Bidirectional toggle for this instance's own local state
  // (e.g. opening/closing its own drawer). Not broadcast — each
  // instance's drawer open/closed state is local UI, not a
  // page-wide truth like login state.
  _toggleState(state, triggerEl) {
    const targets = this.querySelectorAll('[data-flo-state="' + state + '"]');
    targets.forEach((el) => {
      const isVisible = el.getAttribute('data-flo-visible') === 'true';
      el.setAttribute('data-flo-visible', isVisible ? 'false' : 'true');
    });
    if (triggerEl) {
      const expanded = triggerEl.getAttribute('aria-expanded') === 'true';
      triggerEl.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    }
  }

  _isDrawerClosed() {
    const drawerEl = this.querySelector('[data-flo-state="drawer"]');
    return !drawerEl || drawerEl.getAttribute('data-flo-visible') !== 'true';
  }

  /**
   * Flips the drawer away from its preferred open direction (Theme Editor > Shopflo Shop Pass >
   * Primary/Secondary Login Button > Drawer position/Drawer alignment, read from this element's
   * own data-drawer-vertical/data-drawer-horizontal attributes) whenever the trigger is too
   * close to that edge of the viewport for the drawer's own (effectively fixed) size to fit.
   * Runs right before the drawer is revealed, so the flip is already applied on the very first
   * paint - no flash of the wrong position.
   *
   * The decision is resolved once per slot (primary/secondary) per browser session and cached
   * in sessionStorage, rather than re-measuring on every open - the trigger's position relative
   * to the viewport it lives in doesn't change between clicks within the same session, so
   * re-measuring every time is pure waste.
   */
  /** Which of the three independently-styled slots this instance is - see the
   * shopflo-accounts--primary/--secondary/--tertiary modifier classes in
   * snippets/shopflo.liquid's shop_pass case. */
  _resolveSlot() {
    if (this.classList.contains('shopflo-accounts--secondary')) return 'secondary';
    if (this.classList.contains('shopflo-accounts--tertiary')) return 'tertiary';
    return 'primary';
  }

  _positionDrawer() {
    const drawerEl = this.querySelector('[data-flo-state="drawer"]');
    const anchorEl = this.querySelector('[data-flo-state="account-icon"]');
    if (!drawerEl || !anchorEl) return;

    const slot = this._resolveSlot();
    const cacheKey = 'shopflo_account_drawer_position_' + slot;
    let position = this._readCachedDrawerPosition(cacheKey);
    if (!position) {
      position = this._computeDrawerPosition(drawerEl, anchorEl);
      this._writeCachedDrawerPosition(cacheKey, position);
    }
    this._applyDrawerPosition(drawerEl, position);
  }

  _readCachedDrawerPosition(cacheKey) {
    try {
      const raw = window.sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.vertical === 'above' || parsed.vertical === 'below') && (parsed.horizontal === 'left' || parsed.horizontal === 'right')) {
        return parsed;
      }
      return null;
    } catch (e) {
      return null; // storage unavailable (e.g. private browsing) - fall through to computing fresh every time
    }
  }

  _writeCachedDrawerPosition(cacheKey, position) {
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify(position));
    } catch (e) {
      // storage unavailable - nothing to do, next click just recomputes
    }
  }

  /**
   * Measures the drawer's real rendered size while it's still closed,
   * by temporarily forcing it visible-but-invisible (inline
   * `!important` beats the stylesheet's `[data-flo-visible="false"]
   * { display: none !important }` rule - see setPopupHeight/etc. in
   * ShopfloTheme for the same trick). No flash: visibility:hidden
   * never paints anything, and both overrides are removed again
   * before this function returns.
   */
  _measureDrawerSize(drawerEl) {
    drawerEl.style.setProperty('display', 'block', 'important');
    drawerEl.style.setProperty('visibility', 'hidden', 'important');
    const rect = drawerEl.getBoundingClientRect();
    drawerEl.style.removeProperty('display');
    drawerEl.style.removeProperty('visibility');
    return { width: rect.width, height: rect.height };
  }

  _computeDrawerPosition(drawerEl, anchorEl) {
    const anchorRect = anchorEl.getBoundingClientRect();
    const { width, height } = this._measureDrawerSize(drawerEl);
    const viewportW = document.documentElement.clientWidth;
    const viewportH = document.documentElement.clientHeight;

    // Preferred side comes from this instance's own data-drawer-vertical/-horizontal (Theme
    // Editor > Shopflo Shop Pass > Primary/Secondary Login Button > Drawer position/Drawer
    // alignment) - only flips away from it when it genuinely doesn't fit AND the other side has
    // more room, same as the trigger being too close to that edge of the viewport.
    const preferredVertical = this.dataset.drawerVertical === 'above' ? 'above' : 'below';
    const preferredHorizontal = this.dataset.drawerHorizontal === 'left' ? 'left' : 'right';

    const spaceBelow = viewportH - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const spaceForPreferredVertical = preferredVertical === 'above' ? spaceAbove : spaceBelow;
    const spaceForOtherVertical = preferredVertical === 'above' ? spaceBelow : spaceAbove;
    const vertical = (spaceForPreferredVertical >= height || spaceForPreferredVertical >= spaceForOtherVertical)
      ? preferredVertical
      : (preferredVertical === 'above' ? 'below' : 'above');

    const spaceForRightAlign = anchorRect.right;
    const spaceForLeftAlign = viewportW - anchorRect.left;
    const spaceForPreferredHorizontal = preferredHorizontal === 'left' ? spaceForLeftAlign : spaceForRightAlign;
    const spaceForOtherHorizontal = preferredHorizontal === 'left' ? spaceForRightAlign : spaceForLeftAlign;
    const horizontal = (spaceForPreferredHorizontal >= width || spaceForPreferredHorizontal >= spaceForOtherHorizontal)
      ? preferredHorizontal
      : (preferredHorizontal === 'left' ? 'right' : 'left');

    return { vertical, horizontal };
  }

  _applyDrawerPosition(drawerEl, position) {
    drawerEl.classList.toggle('shopflo-accounts__drawer--open-top', position.vertical === 'above');
    drawerEl.classList.toggle('shopflo-accounts__drawer--open-left', position.horizontal === 'left');
  }

  _setOverlayVisible(visible) {
    this.querySelectorAll('[data-flo-state="account-drawer-overlay"]').forEach((el) => {
      el.setAttribute('data-flo-visible', visible ? 'true' : 'false');
    });
  }

  /**
   * Called after the "Account" drawer item's click has fired
   * handleShopifyLogin() — waits for the bundle to append its real
   * (permanently hidden) login iframe, then mirrors it into ours.
   */
  _handleAccountIframeFlow() {
    this._waitForElement(ShopfloAccountsConfig.iframe.sourceId).then((iframeEl) => {
      if (!iframeEl) {
        console.warn(
          '[shopflo-accounts]',
          'Timed out waiting for #' + ShopfloAccountsConfig.iframe.sourceId + ' to appear after account-login click.'
        );
        return;
      }
      this._openIframeFlow(iframeEl);
    });
  }

  /**
   * Desktop: copies the bundle's real iframe src into OUR OWN iframe (a sibling of the drawer)
   * and swaps from the plain menu drawer to the iframe view. Does NOT touch the real iframe
   * itself beyond reading its src — that one stays permanently off-screen (see
   * #flo-shopify-login-iframe in shopflo-styles.css) on desktop only.
   *
   * Mobile: does NOT copy anything — the bundle's own real sheet+overlay component is left
   * fully visible and untouched, so we only hide our own simple menu drawer and overlay, then
   * step out of the way entirely.
   */
  _openIframeFlow(realIframeEl) {
    this._dispatchAccountEvent('account-iframe-opened');

    this.querySelectorAll('[data-flo-state="drawer"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'false');
    });

    const isMobile = window.matchMedia('(max-width: ' + ShopfloAccountsConfig.iframe.mobileBreakpoint + 'px)').matches;
    if (isMobile) {
      // The bundle's real sheet+overlay renders itself — nothing more for us to do. Hide our own
      // overlay too, since the real one already covers that role; showing both would double-dim.
      this._setOverlayVisible(false);
      return;
    }

    const src = realIframeEl.getAttribute('src');
    if (!src) {
      console.warn('[shopflo-accounts]', 'Real iframe #' + ShopfloAccountsConfig.iframe.sourceId + ' has no src yet.');
      return;
    }

    this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
      iframeEl.setAttribute('src', src);
      iframeEl.setAttribute('data-flo-visible', 'true');
    });

    this._setOverlayVisible(true);

    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.setAttribute('data-flo-iframe-active', 'true');
    });

    this._startIframeSizeSync(realIframeEl);
  }

  /**
   * Keeps our copy's size matched to the real content's, which
   * changes at times we don't control (the user interacting inside
   * it). Scoped narrowly — one element, one attribute, only for as
   * long as the iframe view is actually open — and disconnected in
   * _closeIframeFlow, so cost is effectively zero outside that window.
   */
  _startIframeSizeSync(realIframeEl) {
    const applySize = () => {
      // Prefer the literal inline height value (the TARGET the bundle
      // set) over getComputedStyle. If the real iframe animates its
      // height via a CSS transition, getComputedStyle can report a
      // mid-transition frame instead of the destination value. The
      // inline string is immune to that, since it's the target, not
      // the current render. Only fall back to computed style if
      // there's no inline value to read (e.g. height set via a class).
      let height = realIframeEl.style.height;
      if (!height) {
        height = getComputedStyle(realIframeEl).height;
      }

      // Known bad transient value — corrected every time it occurs,
      // not just on a first read, since it can recur on later
      // mutations too. Rounded comparison catches near-232 fractional
      // reads (e.g. 231.992px). Keep the 'px' unit — a unitless
      // number here would make the CSS custom property invalid.
      if (Math.round(parseFloat(height)) === 232) {
        height = '225px';
      }

      this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
        iframeEl.style.setProperty('--flo-sso-iframe-height', height);
      });
    };

    applySize();
    this._iframeSizeObserver = new MutationObserver(applySize);
    this._iframeSizeObserver.observe(realIframeEl, { attributes: true, attributeFilter: ['style'] });

    // Safety net: if height changes via a transition rather than a
    // plain inline jump, re-sync once more once it's actually settled.
    this._iframeSizeRealEl = realIframeEl;
    this._iframeTransitionEndHandler = (event) => {
      if (event.propertyName === 'height') applySize();
    };
    realIframeEl.addEventListener('transitionend', this._iframeTransitionEndHandler);
  }

  _stopIframeSizeSync() {
    if (this._iframeSizeObserver) {
      this._iframeSizeObserver.disconnect();
      this._iframeSizeObserver = null;
    }
    if (this._iframeSizeRealEl && this._iframeTransitionEndHandler) {
      this._iframeSizeRealEl.removeEventListener('transitionend', this._iframeTransitionEndHandler);
    }
    this._iframeSizeRealEl = null;
    this._iframeTransitionEndHandler = null;
  }

  /**
   * Reverses _openIframeFlow: hides our iframe/overlay, resets our iframe back to about:blank
   * (stops it rather than leaving it loaded in the background). Fully collapses rather than
   * restoring the plain menu — matches standard dropdown-toggle behavior.
   */
  _closeIframeFlow() {
    // Called unconditionally whenever the drawer is closed/toggled (not just when the iframe is
    // actually open), so this check keeps account-iframe-closed from firing needlessly - only
    // dispatched when there was really something to close.
    const wasOpen = this.querySelector('[data-flo-state="drawer-iframe"][data-flo-visible="true"]');

    this._stopIframeSizeSync();

    this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
      iframeEl.setAttribute('data-flo-visible', 'false');
      iframeEl.setAttribute('src', 'about:blank');
      iframeEl.style.removeProperty('--flo-sso-iframe-height');
    });

    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.removeAttribute('data-flo-iframe-active');
    });

    if (wasOpen) this._dispatchAccountEvent('account-iframe-closed');
  }

  /**
   * The overlay is only ever shown while the drawer or iframe is
   * open, so a click on it always means "close everything" — this is
   * a force-close, not a toggle.
   */
  _closeDrawer() {
    const wasDrawerOpen = !this._isDrawerClosed();
    this._closeIframeFlow();
    this.querySelectorAll('[data-flo-state="drawer"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'false');
    });
    this._setOverlayVisible(false);
    this.querySelectorAll('[data-flo-trigger="header-icon"]').forEach((el) => {
      if (el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', 'false');
    });
    if (wasDrawerOpen) this._dispatchAccountEvent('account-drawer-closed');
  }
}

if (!customElements.get('shopflo-accounts')) {
  customElements.define('shopflo-accounts', ShopfloAccounts);
}
